'use client';

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { AuthState } from '@/lib/firebase/types';
import getHighestRole from '@/utils/getHighestRole';
import getUser from '@/actions/user/getUser';

const initialState = {
  userData: null,
  highestRole: null,
  roles: [],
  hideSensitiveData: true,
  companySettings: {
    useOwnPatientId: false,
    sharePatients: false,
  },
  userId: null,
  loaded: false,
};

export const authStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,
    setUserData: (userData) => {
      if (!userData) {
        set({ userData: null, loaded: true });
        return;
      }

      const { roles, company, userId } = userData || {};

      const highestRole = getHighestRole(roles);

      const companySettings = {
        useOwnPatientId: company?.useOwnPatientId || false,
        sharePatients: company?.sharePatients || false,
      };

      set({ userData, loaded: true, highestRole, roles, companySettings, userId });
    },
    getUserData: async () => {
      const { userId, setUserData } = get();

      if (!userId) return false;

      const userData = await getUser(userId);

      setUserData(userData);
      return true;
    },
    setHideSensitiveData: (hideSensitiveData: boolean) => set({ hideSensitiveData }),
    reset: () => set(initialState),
  })),
);

const useAuth = (): AuthState => {
  return {
    userData: authStore((state) => state?.userData),
    highestRole: authStore((state) => state.highestRole),
    roles: authStore((state) => state.roles),
    hideSensitiveData: authStore((state) => state.hideSensitiveData),
    companySettings: authStore((state) => state.companySettings),
    userId: authStore((state) => state.userId),
    loaded: authStore((state) => state.loaded),
    setUserData: authStore((state) => state.setUserData),
    getUserData: authStore((state) => state.getUserData),
    setHideSensitiveData: authStore((state) => state.setHideSensitiveData),
    reset: authStore((state) => state.reset),
  };
};

export default useAuth;
